import type { MeResponse } from '../api/admin';

/**
 * The settings pages, grouped as the sidebar draws them (R10, screen 25):
 *
 *   You                  Profile & security
 *   Team                 Users · Groups · Sign-in & access
 *   Workspace            Branding · Integrations · Privacy · Audit log
 *   Billing & developer  Billing & plan · Payments · API
 *
 * ONE list, read by the settings sidebar (SettingsLayout) and the ⌘K index
 * (commandIndex.ts), so the two can never disagree about who may open what.
 * Pages are ABSENT rather than disabled for anyone who may not use them:
 *
 *   Owner    everything.
 *   Admin    everything but the money (Billing & plan, Payments, API).
 *   Member   Profile & security, and Sign-in & access read-only.
 *
 * Groups and the Audit log ride the team-access feature: a page that 402s on
 * arrival is a dead page, and the upsell lives on the plan picker. The server
 * refuses each route either way; this just avoids offering a door that will
 * not open.
 */
export interface SettingsPage {
  to: string;
  label: string;
  /** Extra words ⌘K matches on (the page's old name among them). */
  keywords?: string;
  hidden: boolean;
}
export interface SettingsGroup {
  label: string;
  items: SettingsPage[];
}

type Me = Pick<MeResponse, 'admin' | 'features' | 'isPlatformAdmin'>;

export function settingsAccess(me: Me) {
  const role = me.admin?.role;
  const isPlatform = !!me.isPlatformAdmin;
  const runsWorkspace = isPlatform || role === 'owner' || role === 'admin';
  const canBill = isPlatform || role === 'owner';
  const teamAccess = !!me.features?.teamAccess;
  return { runsWorkspace, canBill, teamAccess };
}

export function settingsNavGroups(me: Me): SettingsGroup[] {
  const { runsWorkspace, canBill, teamAccess } = settingsAccess(me);
  return [
    {
      label: 'You',
      items: [
        // Everyone: your own password and second factor are yours whatever your rung.
        { to: '/admin/settings/profile', label: 'Profile & security', keywords: 'security two-factor 2fa password sessions me', hidden: false },
      ],
    },
    {
      label: 'Team',
      items: [
        { to: '/admin/settings/users', label: 'Users', keywords: 'people team invite seats', hidden: !runsWorkspace },
        { to: '/admin/settings/groups', label: 'Groups', keywords: 'teams folders', hidden: !(runsWorkspace && teamAccess) },
        // Everyone: a Member reads it (the page is read-only for them).
        { to: '/admin/settings/access', label: 'Sign-in & access', keywords: 'account access sso single sign-on identity provider domains', hidden: false },
      ],
    },
    {
      label: 'Workspace',
      items: [
        // Open on every plan: the palette and the default accent are free,
        // and the branded half renders its own upsell.
        { to: '/admin/settings/branding', label: 'Branding', keywords: 'logo colours fonts house style', hidden: !runsWorkspace },
        { to: '/admin/settings/integrations', label: 'Integrations', keywords: 'connect tokens slack monday hubspot', hidden: !runsWorkspace },
        // Erasure is a legal obligation, so no plan gate.
        { to: '/admin/settings/privacy', label: 'Privacy', keywords: 'erasure gdpr delete person ai', hidden: !runsWorkspace },
        { to: '/admin/settings/audit', label: 'Audit log', keywords: 'audit trail history export', hidden: !(runsWorkspace && teamAccess) },
      ],
    },
    {
      label: 'Billing & developer',
      items: [
        { to: '/admin/settings/billing', label: 'Billing & plan', keywords: 'billing invoices plan subscription', hidden: !canBill },
        { to: '/admin/settings/payments', label: 'Payments', keywords: 'stripe connect refunds', hidden: !canBill },
        { to: '/admin/settings/api', label: 'API', keywords: 'api keys developer tokens curl', hidden: !canBill },
      ],
    },
  ];
}

/** The pages this person can open, in the sidebar's order. */
export function visibleSettingsPages(me: Me): SettingsPage[] {
  return settingsNavGroups(me).flatMap((g) => g.items.filter((it) => !it.hidden));
}

/**
 * Where "Account settings" lands: the first page about the account rather
 * than about you — Users for an Owner or Admin, Sign-in & access for a Member.
 */
export function accountSettingsHome(me: Me): string {
  return visibleSettingsPages(me).find((p) => p.to !== '/admin/settings/profile')?.to || '/admin/settings/access';
}
